import { useEffect, useState } from 'react';
import { Download, Filter, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#F97316', '#FDBA74', '#10B981', '#64748B', '#EF4444', '#3B82F6'];

const Reports = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    project_id: 'all',
    category_id: 'all',
  });

  useEffect(() => {
    loadMasterData();
    loadReport();
  }, []);

  const loadMasterData = async () => {
    try {
      const [projectsRes, categoriesRes] = await Promise.all([
        api.get('/projects'),
        api.get('/categories?type=expense'),
      ]);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', new Date(filters.start_date).toISOString());
      if (filters.end_date) params.append('end_date', new Date(filters.end_date).toISOString());
      if (filters.project_id && filters.project_id !== 'all') params.append('project_id', filters.project_id);
      if (filters.category_id && filters.category_id !== 'all') params.append('category_id', filters.category_id);

      const response = await api.get(`/reports/comprehensive?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const downloadExpensePDF = async () => {
    try {
      const response = await api.get(
        `/reports/expenses/pdf?start_date=${new Date(filters.start_date).toISOString()}&end_date=${new Date(filters.end_date).toISOString()}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_report_${filters.start_date}_to_${filters.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading && !reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header title="Comprehensive Reports" subtitle="Advanced analytics with custom filters and export" />

      <div className="p-8">
        {/* Filters Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-orange-600" />
              Report Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="bg-slate-50"
                  data-testid="filter-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="bg-slate-50"
                  data-testid="filter-end-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Project (Optional)</Label>
                <Select value={filters.project_id} onValueChange={(value) => setFilters({ ...filters, project_id: value === 'all' ? '' : value })}>
                  <SelectTrigger className="bg-slate-50" data-testid="filter-project">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category (Optional)</Label>
                <Select value={filters.category_id} onValueChange={(value) => setFilters({ ...filters, category_id: value === 'all' ? '' : value })}>
                  <SelectTrigger className="bg-slate-50" data-testid="filter-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={loadReport}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                disabled={loading}
                data-testid="apply-filters-button"
              >
                {loading ? 'Loading...' : 'Apply Filters'}
              </Button>
              <Button
                onClick={downloadExpensePDF}
                variant="outline"
                className="border-slate-200"
                data-testid="download-pdf-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {reportData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-500">Total Revenue</p>
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="text-3xl font-heading font-bold text-emerald-600">
                    {formatCurrency(reportData.summary.total_revenue)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-500">Total Expenses</p>
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <p className="text-3xl font-heading font-bold text-red-600">
                    {formatCurrency(reportData.summary.total_expenses)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-500">Net Profit/Loss</p>
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <p className={`text-3xl font-heading font-bold ${reportData.summary.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.summary.net_profit)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-500">Active Projects</p>
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-3xl font-heading font-bold text-slate-900">
                    {reportData.summary.active_projects} / {reportData.summary.total_projects}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Split — Vayah Vikas vs Other Projects */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-slate-700">Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-xl p-5">
                    <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">Vayah Vikas</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(reportData.summary.vayah_vikas_revenue)}</p>
                    <p className="text-xs text-blue-400 mt-1">
                      {reportData.summary.total_revenue > 0
                        ? Math.round((reportData.summary.vayah_vikas_revenue / reportData.summary.total_revenue) * 100)
                        : 0}% of total revenue
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-5">
                    <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide mb-1">Other Projects</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(reportData.summary.other_revenue)}</p>
                    <p className="text-xs text-emerald-400 mt-1">
                      {reportData.summary.total_revenue > 0
                        ? Math.round((reportData.summary.other_revenue / reportData.summary.total_revenue) * 100)
                        : 0}% of total revenue
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-5">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Split</p>
                    <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: reportData.summary.total_revenue > 0
                            ? `${(reportData.summary.vayah_vikas_revenue / reportData.summary.total_revenue) * 100}%`
                            : '0%'
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-slate-500">
                      <span className="text-blue-500 font-medium">Vayah Vikas</span>
                      <span className="text-emerald-500 font-medium">Other Projects</span>
                    </div>
                  </div>
                </div>

                {/* Per-project bar chart */}
                {reportData.project_revenue.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Revenue by Project</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={[...reportData.project_revenue].sort((a, b) => b.value - a.value)}
                        layout="vertical"
                        margin={{ left: 120, right: 20, top: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" stroke="#64748B" style={{ fontSize: '11px' }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" stroke="#64748B" style={{ fontSize: '11px' }} width={115} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                          {[...reportData.project_revenue]
                            .sort((a, b) => b.value - a.value)
                            .map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={entry.name === 'Vayah Vikas' ? '#3B82F6' : '#F97316'}
                              />
                            ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle>Expenses by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  {reportData.expense_by_category.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={reportData.expense_by_category}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => entry.name}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {reportData.expense_by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-20">No data available</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Expenses by Person</CardTitle>
                </CardHeader>
                <CardContent>
                  {reportData.expense_by_source.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={reportData.expense_by_source}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: '12px' }} />
                        <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="value" fill="#F97316" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-20">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Detailed Expenses Table */}
            <Card>
              <CardHeader>
                <CardTitle>Detailed Expense Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="detailed-expenses-table">
                    <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-4 text-left">Date</th>
                        <th className="px-6 py-4 text-left">Category</th>
                        <th className="px-6 py-4 text-left">Description</th>
                        <th className="px-6 py-4 text-right">Amount</th>
                        <th className="px-6 py-4 text-left">Paid By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.detailed_expenses.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                            No expenses found for selected filters
                          </td>
                        </tr>
                      ) : (
                        reportData.detailed_expenses.map((expense) => (
                          <tr key={expense.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                              {format(new Date(expense.date), 'MMM dd, yyyy')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                              {expense.category_name}
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-700">{expense.description}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-red-600 font-semibold">
                              {formatCurrency(expense.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                              {expense.paid_by_name}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
